import React, { useState, useEffect, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { Target, Edit3 } from 'lucide-react';
import { budgetApi, AnnualBudget } from '../../../api/budgetApi';
import { BudgetEditModal } from './BudgetEditModal';
import { useUsersData } from '../../../Helpers/UsersContext';
import { CostCategory } from '../../../Helpers/maintenanceCostUtils';
import { COST_CATEGORY_LABELS, COST_CATEGORY_COLORS } from '../../../Helpers/chartColors';

interface BudgetCardProps {
    /** Total projected cost for the current year (from yearlyPlans) */
    currentYearCost: number;
    /** Current-year projected cost broken down by category (for the Prognos hover) */
    currentYearCostByCategory?: Record<CostCategory, number>;
    /** Callback when budget is updated (to refresh parent if needed) */
    onBudgetUpdated?: () => void;
}

export const BudgetCard: React.FC<BudgetCardProps> = ({
    currentYearCost,
    currentYearCostByCategory,
    onBudgetUpdated
}) => {
    const [showBreakdown, setShowBreakdown] = useState(false);
    const currentYear = new Date().getFullYear();
    const auth = getAuth();
    const { users } = useUsersData();

    const [budget, setBudget] = useState<AnnualBudget | null>(null);
    const [loading, setLoading] = useState(true);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editSubmitting, setEditSubmitting] = useState(false);

    // Check if current user is admin
    const isAdmin = users?.find(u => u.key === auth.currentUser?.uid)?.admin || false;

    // Load budget
    const loadBudget = useCallback(async () => {
        try {
            const data = await budgetApi.getBudget(currentYear);
            setBudget(data);
        } catch (err) {
            console.error('Error loading budget:', err);
        } finally {
            setLoading(false);
        }
    }, [currentYear]);

    useEffect(() => {
        loadBudget();
    }, [loadBudget]);

    // Handle save
    const handleSaveBudget = async (amount: number) => {
        setEditSubmitting(true);
        try {
            await budgetApi.setBudget(currentYear, amount);
            await loadBudget();
            onBudgetUpdated?.();
        } finally {
            setEditSubmitting(false);
        }
    };

    // Calculate values
    const budgetAmount = budget?.amount ?? 0;
    const remaining = budgetAmount - currentYearCost;
    const isOverBudget = remaining < 0;
    const utilizationPercent = budgetAmount > 0
        ? Math.round((currentYearCost / budgetAmount) * 100)
        : 0;
    const displayPercent = Math.min(utilizationPercent, 100); // Cap progress bar at 100%

    // Format currency
    const formatCurrency = (value: number) =>
        `${Math.round(Math.abs(value)).toLocaleString('sv-SE')} kr`;

    // Non-zero category rows for the Prognos hover breakdown
    const breakdownEntries = currentYearCostByCategory
        ? (Object.entries(currentYearCostByCategory) as [CostCategory, number][])
            .filter(([, value]) => Math.round(value) !== 0)
        : [];

    if (loading) {
        return (
            <div className="std-box dropshadow" style={{ padding: '24px' }}>
                <div className="text-center text-muted">Laddar budget...</div>
            </div>
        );
    }

    // No budget set state
    if (!budget) {
        return (
            <>
                <div className="std-box dropshadow" style={{ padding: '24px' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Target size={20} />
                            Årsbudget {currentYear}
                        </h5>
                        {isAdmin && (
                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => setShowEditModal(true)}
                            >
                                <Edit3 size={14} className="me-1" />
                                Sätt budget
                            </button>
                        )}
                    </div>
                    <div className="text-center text-muted py-3">
                        {isAdmin
                            ? 'Ingen budget satt. Klicka på "Sätt budget" för att komma igång.'
                            : 'Ingen budget satt för detta år.'}
                    </div>
                </div>
                <BudgetEditModal
                    show={showEditModal}
                    onHide={() => setShowEditModal(false)}
                    onSave={handleSaveBudget}
                    submitting={editSubmitting}
                />
            </>
        );
    }

    return (
        <>
            <div className="std-box dropshadow" style={{ padding: '24px' }}>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Target size={20} />
                        Årsbudget {currentYear}
                    </h5>
                    {isAdmin && (
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setShowEditModal(true)}
                        >
                            <Edit3 size={14} className="me-1" />
                            Redigera
                        </button>
                    )}
                </div>

                {/* Summary values */}
                <div className="row text-center mb-3">
                    <div className="col-4">
                        <div className="text-muted small">Budget</div>
                        <div className="fw-bold" style={{ fontSize: '1.25rem' }}>
                            {formatCurrency(budgetAmount)}
                        </div>
                    </div>
                    <div className="col-4">
                        <div className="text-muted small">Prognos</div>
                        <div
                            className="fw-bold"
                            style={{
                                fontSize: '1.25rem',
                                position: 'relative',
                                display: 'inline-block',
                                cursor: breakdownEntries.length > 0 ? 'help' : 'default'
                            }}
                            onMouseEnter={() => setShowBreakdown(true)}
                            onMouseLeave={() => setShowBreakdown(false)}
                        >
                            {formatCurrency(currentYearCost)}
                            {showBreakdown && breakdownEntries.length > 0 && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        marginTop: '6px',
                                        background: '#fff',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '6px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        padding: '8px 10px',
                                        zIndex: 20,
                                        minWidth: '220px',
                                        textAlign: 'left',
                                        fontWeight: 400
                                    }}
                                >
                                    {breakdownEntries.map(([category, value]) => (
                                        <div
                                            key={category}
                                            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8125rem', padding: '1px 0' }}
                                        >
                                            <span style={{
                                                width: '9px', height: '9px', borderRadius: '50%', flexShrink: 0,
                                                backgroundColor: COST_CATEGORY_COLORS[category] || '#ccc'
                                            }} />
                                            <span style={{ flex: 1, color: '#444', whiteSpace: 'nowrap' }}>
                                                {COST_CATEGORY_LABELS[category] || category}
                                            </span>
                                            <span style={{ fontWeight: 600 }}>
                                                {Math.round(value).toLocaleString('sv-SE')} kr
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="col-4">
                        <div className="text-muted small">
                            {isOverBudget ? 'Överskridande' : 'Kvar'}
                        </div>
                        <div
                            className="fw-bold"
                            style={{
                                fontSize: '1.25rem',
                                color: isOverBudget ? '#dc3545' : '#198754'
                            }}
                        >
                            {formatCurrency(remaining)}
                        </div>
                    </div>
                </div>

                {/* Progress bar */}
                <div>
                    <div className="progress" style={{ height: '12px' }}>
                        <div
                            className="progress-bar"
                            role="progressbar"
                            style={{
                                width: `${displayPercent}%`,
                                backgroundColor: isOverBudget ? '#dc3545' : '#198754',
                                transition: 'width 0.3s ease'
                            }}
                            aria-valuenow={utilizationPercent}
                            aria-valuemin={0}
                            aria-valuemax={100}
                        />
                    </div>
                    <div className="text-center mt-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                        {utilizationPercent}% av budget
                    </div>
                </div>
            </div>

            <BudgetEditModal
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                onSave={handleSaveBudget}
                currentAmount={budget.amount}
                submitting={editSubmitting}
            />
        </>
    );
};
